import type {
  ToothAxialLevels,
  ToothDepthMapping,
  ToothExtentsProfiles,
} from '../config';
import {
  DEFAULT_CARD_AXIAL_CROWN_INSET_MM,
  TOOTH_AXIAL_LEVEL_MIN_TISSUE_BINS,
  TOOTH_AXIAL_CORONAL_ROOT_LEVEL_RATIO,
  TOOTH_AXIAL_ROOT_LEVEL_RATIO,
  TOOTH_CROWN_LENGTH_RATIO,
  TOOTH_TISSUE_MASS_FLOOR_RATIO,
} from '../config';

import { toothDepthMmOfRatio } from './toothDepthMapping';

type TissueSpan = {
  fromBin: number;
  isCrownFirst: boolean;
  toBin: number;
};

const resolveTissueSpan = (masses: number[]): TissueSpan | null => {
  const peak = Math.max(...masses);

  if (peak <= 0) {
    return null;
  }

  const floor = peak * TOOTH_TISSUE_MASS_FLOOR_RATIO;
  const fromBin = masses.findIndex((mass) => mass > floor);
  const toBin = masses.reduce(
    (last, mass, index) => (mass > floor ? index : last),
    -1,
  );

  if (fromBin < 0 || toBin - fromBin < TOOTH_AXIAL_LEVEL_MIN_TISSUE_BINS) {
    return null;
  }

  const quarter = Math.max(1, Math.round((toBin - fromBin + 1) / 4));
  const sumOf = (from: number, to: number) =>
    masses.slice(from, to).reduce((total, mass) => total + mass, 0);

  return {
    fromBin,
    isCrownFirst:
      sumOf(fromBin, fromBin + quarter) >=
      sumOf(toBin - quarter + 1, toBin + 1),
    toBin,
  };
};

export const resolveToothAxialLevels = ({
  axialOffsetsMm,
  profiles,
}: {
  axialOffsetsMm: number[];
  profiles: ToothExtentsProfiles | null;
}): ToothAxialLevels | null => {
  if (!profiles || axialOffsetsMm.length < 2) {
    return null;
  }

  const masses = profiles.z;
  const span = resolveTissueSpan(masses);

  if (!span) {
    return null;
  }

  const tissueSpanMm = (span.toBin - span.fromBin) * profiles.binMm;
  const neckRatio = TOOTH_CROWN_LENGTH_RATIO;

  const depth: ToothDepthMapping = {
    crownOffsetMm: axialOffsetsMm[0],
    fromBin: span.fromBin,
    isCrownFirst: span.isCrownFirst,
    spanMm: tissueSpanMm,
    toBin: span.toBin,
  };
  const depthAt = (ratio: number) => toothDepthMmOfRatio({ depth, ratio });

  const belowNeck = (share: number) => neckRatio + (1 - neckRatio) * share;
  const halfCrownMm = (neckRatio / 2) * tissueSpanMm;

  return {
    cejMm: depthAt(neckRatio),
    coronalRootMm: depthAt(belowNeck(TOOTH_AXIAL_CORONAL_ROOT_LEVEL_RATIO)),
    crownMm:
      depth.crownOffsetMm +
      Math.min(DEFAULT_CARD_AXIAL_CROWN_INSET_MM, halfCrownMm),
    depth,
    neckRatio,
    rootMm: depthAt(belowNeck(TOOTH_AXIAL_ROOT_LEVEL_RATIO)),
  };
};
